import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Upload, Loader2, X, FolderOpen, FileDown, Trash2, Lock, Scale } from "lucide-react";
import { api, downloadFile, fmtDate, STATUS_STYLES } from "@/lib/api";

const field = "w-full rounded-none border-0 border-b-2 border-navy/15 bg-transparent px-0 py-2 text-sm text-navy shadow-none focus:border-gold focus:outline-none";
const label = "mb-1 block font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/60";

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [meta, setMeta] = useState({ categories: [] });
  const [form, setForm] = useState({ name: "", category: "", owner: "", version: "1.0", effective_date: "", expiration_date: "", notes: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [params] = useSearchParams();

  const load = () => {
    api.get("/api/app/documents").then((r) => setDocs(r.data));
    api.get("/api/app/documents/meta").then((r) => setMeta(r.data));
  };
  useEffect(load, []);

  useEffect(() => {
    if (params.get("new")) setTimeout(() => document.querySelector("[data-testid=document-upload-name]")?.scrollIntoView({ block: "center" }), 300);
  }, [params]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) {
      setError("Choose a file to upload.");
      return;
    }
    setBusy(true);
    setError("");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    try {
      await api.post("/api/app/documents", fd);
      setForm({ name: "", category: "", owner: "", version: "1.0", effective_date: "", expiration_date: "", notes: "" });
      setFile(null);
      e.target?.reset?.();
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed. PDF, DOCX, XLSX, or image files up to 10 MB.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/api/app/documents/${id}`);
    load();
  };

  return (
    <div className="space-y-8" data-testid="documents-page">
      <div>
        <p className="eyebrow">Document Control Center</p>
        <h1 className="mt-3 font-serif text-4xl font-semibold text-navy">Documents & Contract Repository</h1>
        <p className="mt-2 max-w-2xl text-sm text-ink/60">
          Secure, searchable storage for registrations, licenses, insurance certificates, contracts, certifications, and
          approved SOPs — with owners, versions, and expiration tracking.
        </p>
      </div>

      <div className="grid gap-5 md:grid-cols-2" data-testid="documents-notices">
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />
          <p>Do not upload Social Security numbers, government ID images, medical records, or other highly sensitive personal
             information. Keep employee identity documents in your official verification systems.</p>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-gold/40 bg-gold/5 p-5 text-sm text-navy/75">
          <Scale className="mt-0.5 h-4 w-4 shrink-0 text-golddark" />
          <p>Contracts are stored and tracked for operational awareness only. MITIVISE does not draft, modify, or interpret
             agreements — that analysis is not a legal opinion. Consult a qualified attorney for legal review.</p>
        </div>
      </div>

      <form onSubmit={upload} className="rounded-lg border border-navy/10 bg-white p-6" data-testid="document-upload-form">
        <h2 className="font-serif text-2xl font-semibold text-navy">Upload a document</h2>
        <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className={label}>Document name *</label>
            <input required className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="document-upload-name" placeholder="General liability COI" />
          </div>
          <div>
            <label className={label}>Category *</label>
            <select required className={field} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="document-upload-category">
              <option value="">Select…</option>
              {meta.categories?.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={label}>Owner</label>
            <input className={field} value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} data-testid="document-upload-owner" />
          </div>
          <div>
            <label className={label}>Version</label>
            <input className={field} value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} data-testid="document-upload-version" />
          </div>
          <div>
            <label className={label}>Effective date</label>
            <input type="date" className={field} value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} data-testid="document-upload-effective" />
          </div>
          <div>
            <label className={label}>Expiration date</label>
            <input type="date" className={field} value={form.expiration_date} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} data-testid="document-upload-expiration" />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <label className={label}>File * (PDF, DOCX, XLSX, or image · max 10 MB)</label>
            <input type="file" required onChange={(e) => setFile(e.target.files[0])} className="block w-full text-sm text-ink/70 file:mr-4 file:rounded-sm file:border-0 file:bg-navy file:px-5 file:py-2.5 file:text-sm file:font-semibold file:text-white hover:file:bg-navydeep" data-testid="document-upload-file" />
          </div>
        </div>
        {error && <p className="mt-4 rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="document-upload-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="document-upload-submit" className="btn-gold mt-6 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Upload Document
        </button>
      </form>

      <div className="overflow-x-auto rounded-lg border border-navy/10 bg-white" data-testid="documents-table">
        <table className="w-full min-w-[860px] text-left text-sm">
          <thead>
            <tr className="border-b border-navy/10 bg-paper font-mono text-[10px] uppercase tracking-[0.14em] text-navy/50">
              {["Document", "Category", "Owner", "Version", "Effective", "Expiration", "Status", "Actions"].map((h) => (
                <th key={h} className="px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-ink/50">No documents yet. Upload your business registration to start your inventory.</td></tr>
            )}
            {docs.map((d) => (
              <tr key={d.id} className="border-b border-navy/5 hover:bg-paper/60" data-testid={`document-row-${d.id}`}>
                <td className="max-w-xs px-4 py-3 font-medium text-navy">{d.name}</td>
                <td className="px-4 py-3 text-ink/60">{d.category}</td>
                <td className="px-4 py-3 text-ink/60">{d.owner || "—"}</td>
                <td className="px-4 py-3 text-ink/60">v{d.version}</td>
                <td className="px-4 py-3 text-ink/60">{fmtDate(d.effective_date)}</td>
                <td className="px-4 py-3 text-ink/60">{fmtDate(d.expiration_date)}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${STATUS_STYLES[d.status] || "bg-navy/5 text-navy/60 border-navy/10"}`}>{d.status}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="flex gap-3">
                    <button onClick={() => downloadFile(`/api/app/documents/${d.id}/download`, d.original_filename)} data-testid={`document-download-${d.id}`} title="Download" className="text-golddark hover:text-navy"><FileDown className="h-4 w-4" /></button>
                    <button onClick={() => remove(d.id)} data-testid={`document-delete-${d.id}`} title="Remove" className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs italic text-ink/45">
        Reminder cadence (60/30/14/7-day expiration reminders) activates with Phase 2 automated notifications. Expiration
        statuses are already tracked and shown above.
      </p>
    </div>
  );
}
